import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  createBot,
  deleteBot,
  getBotChats,
  getBots,
  getChatMessages,
  updateBot,
  updateChatAssistantName,
  verifyBot,
} from "../../../services/api/instances/botApi/methods";
import {
  BotObject,
  ChatbotValue,
  ChatObject,
} from "../../../services/api/instances/botApi/types";
import { getBotObjects, isTokenExist, processList } from "../helpers";
import { RootState } from "../../../services/redux/types";
import {
  addStorageObject,
  editStorageObject,
} from "../../../app/state/storageObjectSlice/actions/actions";
import { NormalizedStorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import i18n from "../../../services/i18n";
import { getAllStorageObjects } from "../../../services/api/instances/storageApi/methods";
import { getUpdatedChatbotValue } from "../helpers/getUpdatedChatbotValue";
import { normalizeStorageObjects } from "../../../app/state/storageObjectSlice/utils/normalizeStorageObjects/normalizeStorageObjects";
import { createNotification } from "../../../app/state/notificationSlice/slice";
import { getError } from "../../../services/lib/getError";

export const botMigrationToStorageObjectAction = createAsyncThunk(
  "chatbot/botMigrationToStorageObject",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const bots = await getBots();
      const botObjects = await getAllStorageObjects("ai_agents_telegram_bots");
      const normalizedBotObjects = normalizeStorageObjects(botObjects);
      const today = new Date();
      const monthAgo = new Date();
      monthAgo.setMonth(today.getMonth() - 1);
      let i = 0;

      for (const bot of bots.tg_bots) {
        const target = Object.values(normalizedBotObjects.entities).find(
          (t) =>
            (t.value as ChatbotValue)?.tg_bot_object.bot_api === bot.bot_api,
        );
        if (!target) {
          dispatch(
            addStorageObject({
              name: `${i18n.t("chatbot.default_name")} ${(i += 1)}`,
              service: "ai_agents_telegram_bots",
              objectType: "child",
              value: {
                tg_bot_object: {
                  ...bot,
                  start_message: `${i18n.t("chatbot.default_welcome_message")}`,
                  secure_bot_name: null,
                  selectedChat: "emptyId",
                  dateStart: monthAgo,
                  dateEnd: today,
                },
              },
              withoutCount: true,
            }),
          );
        }
      }
      return;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createChatbotObjectAction = createAsyncThunk(
  "chatbot/createChatbotObject",
  async (tg_token: string, { getState, dispatch, rejectWithValue }) => {
    let chatbotValue: ChatbotValue | null = null;
    try {
      const { data } = (getState() as RootState).storageObjectState
        .ai_agents_telegram_bots;
      const today = new Date();
      const monthAgo = new Date();
      monthAgo.setMonth(today.getMonth() - 1);
      const bots = getBotObjects(
        data.entities as Record<string, NormalizedStorageObject<ChatbotValue>>,
      );
      const bot = await verifyBot(tg_token as string).catch(() => {
        if (isTokenExist(tg_token, bots)) {
          throw new Error();
        }
        dispatch(createNotification("error", "Invalid token"));
        throw new Error();
      });
      const newBot = await createBot({
        bot_api: tg_token as string,
        bot_name: bot?.result?.username as string,
      });
      await updateBot(newBot?.ref, {
        ...newBot,
        start_message: `${i18n.t("chatbot.default_welcome_message")}`,
        secure_bot_name: `${i18n.t("chatbot.no_bot-placeholder")}`,
      });
      chatbotValue = {
        tg_bot_object: {
          ...newBot,
          start_message: `${i18n.t("chatbot.default_welcome_message")}`,
          secure_bot_name: `${i18n.t("chatbot.no_bot-placeholder")}`,
          selectedChat: "emptyId",
          dateStart: monthAgo,
          dateEnd: today,
        },
      };

      dispatch(
        addStorageObject({
          name: i18n.t("chatbot.default_name"),
          service: "ai_agents_telegram_bots",
          objectType: "child",
          value: chatbotValue,
        }),
      );
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getBotChatsAction = createAsyncThunk(
  "chatbot/getBotChatsWithoutMessages",
  async (ref: string, { rejectWithValue }) => {
    try {
      const chats = await getBotChats(ref);
      return { chats, ref };
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getBotMessagesAction = createAsyncThunk(
  "chatbot/getBotMessages",
  async (body: { ref: string; chatId: string }, { rejectWithValue }) => {
    try {
      const { ref, chatId } = body;
      const messages = await getChatMessages(ref, chatId);
      const stateMessages = messages.messages
        .map((item) => ({
          role: item.role,
          content: item.message_text,
        }))
        .reverse();
      return stateMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateChatbotAction = createAsyncThunk(
  "chatbot/updateChatbot",
  async (bot: BotObject, { rejectWithValue }) => {
    try {
      const { selectedChat, dateStart, dateEnd, ...rest } = bot;

      const postBot = {
        ...rest,
        black_list: processList(rest?.black_list || null),
        white_list: processList(rest?.white_list || null),
      };

      return await updateBot(postBot.ref, postBot as BotObject);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateChatbotAssistantAction = createAsyncThunk(
  "chatbot/updateChatbotAssistant",
  async (body: { chatId: string; assistantId: string }, ThunkApi) => {
    try {
      const { chatId, assistantId } = body;
      const { chatbotValue, chats } = (ThunkApi.getState() as RootState)
        .chatbotState;
      const bot_value = chatbotValue?.tg_bot_object;
      if (!bot_value) return;
      const ref = bot_value.ref;
      const newAssistantId =
        assistantId === bot_value?.asst_name ? "def" : assistantId;
      const updatedChats = chats[bot_value.ref].map((chat) => {
        if (chat.chat_id === chatId) {
          return {
            ...chat,
            asst_name: newAssistantId,
          };
        }
        return chat;
      }) as ChatObject[];

      await updateChatAssistantName(ref, chatId, {
        asst_name: newAssistantId,
      });
      return { updatedChats, ref };
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkApi.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const changeBotApiAction = createAsyncThunk(
  "chatbot/changeBotApi",
  async (token: string, { getState, dispatch, rejectWithValue }) => {
    try {
      const { data, selectedId } = (getState() as RootState).storageObjectState
        .ai_agents_telegram_bots;
      const { chatbotValue } = (getState() as RootState).chatbotState;
      if (!chatbotValue || !selectedId) return;

      const selectedObject = data.entities[selectedId];
      const bots = getBotObjects(
        data.entities as Record<string, NormalizedStorageObject<ChatbotValue>>,
      );
      const bot = await verifyBot(token as string).catch(() => {
        if (isTokenExist(token, bots)) {
          throw new Error();
        }
        dispatch(createNotification("error", "Invalid token"));
        throw new Error();
      });

      const botsWithOldSecureBotValue = Object.values(data.entities).filter(
        (item) =>
          item.value !== null &&
          (item.value as ChatbotValue).tg_bot_object.secure_bot_name ===
            chatbotValue?.tg_bot_object?.bot_name,
      );

      botsWithOldSecureBotValue.forEach((b) => {
        const newValue = getUpdatedChatbotValue(chatbotValue, {
          secure_bot_name: null,
        });
        dispatch(
          editStorageObject({
            id: b.id,
            name: b.friendly_name,
            service: "ai_agents_telegram_bots",
            value: newValue,
          }),
        );
      });

      const updatedValue = getUpdatedChatbotValue(chatbotValue, {
        bot_api: token,
        bot_name: bot.result?.username,
        secure_bot_name: null,
      });

      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject.friendly_name,
          service: "ai_agents_telegram_bots",
          value: updatedValue,
        }),
      );

      return;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteChatbotAction = createAsyncThunk(
  "chatbot/deleteChatbot",
  async (ref: string, { rejectWithValue }) => {
    try {
      return await deleteBot(ref);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
